var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState, } from 'react';
import { Form } from 'antd';
import { fetchSpeaker } from 'api/speaker-api-v2';
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import { editSpeaker } from '../../speaker-actions';
import { getFullName } from '../../speaker-utils';
import { getSpeakerNodesFromDb, getSpeakerNodesFromDocument } from '../../speaker-nodes';
import AvailabilitySettings from '../../AvailabilitySettings';
import SpeakerDetails from '../../SpeakerDetails';
import SpeakerForm from '../../SpeakerForm';
import ColorPicker from '../../ColorPicker';
import useNameVariantEntries from './useNameVariantEntries';
import NameVariantWithRole from './NameVariantWithRole';
import { ReplaceMode } from '../../types';
import { SpeakerTreeSelect } from '../../SpeakerTreeSelect';
const EditTab = ({ form, mode, language, onClose, isCaptionMode, defaultSpeakerColor, setSubmitState, replacedSpeaker, editedId, editedFirstName, editedLastName, openAddRoleModal, setTabChangeData, onSubmit, getEditorSpeaker, }) => {
    var _a;
    const [speakerData, setSpeakerData] = useState(null);
    const [pinnedNodes, setPinnedNodes] = useState([]);
    const [documentRole, setDocumentRole] = useState(null);
    const { session } = useSession();
    const isSpeakerIdEnabled = session.login.hasClaim('speakerId:enabled');
    const formAvailability = Form.useWatch('availability', form);
    const isCurrent = formAvailability === 'current';
    const isReplacedMode = mode !== 'edit';
    const langCode = (_a = language === null || language === void 0 ? void 0 : language.code) !== null && _a !== void 0 ? _a : '';
    const { nameVariant, role, init: initNameVariantEntries, entries: nameVariantEntries, } = useNameVariantEntries(langCode);
    const fetchSpeakerFromDb = useCallback(async (speakerDbid) => {
        const result = await fetchSpeaker(session.connection, speakerDbid);
        return result.isSuccess() ? result.get() : null;
    }, [session.connection]);
    const handleEditIdChange = useCallback((id, variantFirstName, variantLastName) => {
        let speakerNode = null;
        void fetchSpeakerFromDb(id).then((dbSpeakerData) => {
            var _a, _b, _c, _d, _e;
            if (dbSpeakerData !== null) {
                setSpeakerData(dbSpeakerData);
                const nameVariantNodes = getSpeakerNodesFromDb(dbSpeakerData, langCode);
                const matchedVariant = variantLastName !== undefined
                    ? nameVariantNodes.find((node) => node.firstName === variantFirstName && node.lastName === variantLastName)
                    : undefined;
                speakerNode = matchedVariant !== undefined
                    ? matchedVariant
                    : ((_a = nameVariantNodes[0]) !== null && _a !== void 0 ? _a : null);
            }
            else {
                setSpeakerData(null);
                const editorSpeaker = (_b = getEditorSpeaker === null || getEditorSpeaker === void 0 ? void 0 : getEditorSpeaker(id)) !== null && _b !== void 0 ? _b : null;
                setDocumentRole((_c = editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.role) !== null && _c !== void 0 ? _c : null);
                if (editorSpeaker !== null) {
                    const [node] = getSpeakerNodesFromDocument(editorSpeaker);
                    speakerNode = node;
                }
            }
            if (speakerNode !== null) {
                const pinnedNode = speakerNode;
                setPinnedNodes((prev) => {
                    if (prev.some((n) => n.dbid === pinnedNode.dbid)) {
                        return prev;
                    }
                    return [...prev, pinnedNode];
                });
                const { children } = speakerNode, newSpeaker = __rest(speakerNode, ["children"]);
                const roleValue = newSpeaker.role !== null
                    ? { label: newSpeaker.role, isDefault: true }
                    : children === null || children === void 0 ? void 0 : children.find((ch) => ch.isDefault);
                const speakerIdActive = isSpeakerIdEnabled
                    ? (_d = dbSpeakerData === null || dbSpeakerData === void 0 ? void 0 : dbSpeakerData.localizations[langCode].speakerIdActive) !== null && _d !== void 0 ? _d : false
                    : false;
                form.setFieldsValue(Object.assign({ speaker: {
                        speaker: speakerNode,
                        roleIndex: 'none',
                        firstName: newSpeaker.firstName,
                        lastName: newSpeaker.lastName,
                    }, role: roleValue != null ? roleValue : null, availability: dbSpeakerData ? 'all' : 'current', speakerIdActive, imageBase64: (_e = dbSpeakerData === null || dbSpeakerData === void 0 ? void 0 : dbSpeakerData.neutralData.imageBase64) !== null && _e !== void 0 ? _e : undefined }, (dbSpeakerData
                    ? {
                        nameVariant: {
                            label: getFullName(newSpeaker.firstName, newSpeaker.lastName),
                            values: {
                                firstName: newSpeaker.firstName,
                                lastName: newSpeaker.lastName,
                            },
                            isDefault: newSpeaker.isDefault,
                        },
                    }
                    : {})));
            }
            else {
                form.setFieldValue('speaker', null);
            }
        });
    }, [fetchSpeakerFromDb, langCode, getEditorSpeaker, form, setTabChangeData, setDocumentRole]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (replacedSpeaker !== undefined) {
            const { editorSpeaker, dbSpeaker } = replacedSpeaker;
            setDocumentRole((_a = editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.role) !== null && _a !== void 0 ? _a : null);
            let speakerNode = null;
            if (dbSpeaker !== null) {
                speakerNode = (_b = getSpeakerNodesFromDb(dbSpeaker, langCode)[0]) !== null && _b !== void 0 ? _b : null;
                setSpeakerData(dbSpeaker);
            }
            else if (editorSpeaker !== null) {
                const [editorNode] = getSpeakerNodesFromDocument(editorSpeaker);
                speakerNode = editorNode;
            }
            if (speakerNode !== null) {
                const defaultChild = (_d = (_c = speakerNode.children) === null || _c === void 0 ? void 0 : _c.find((ch) => ch.isDefault)) !== null && _d !== void 0 ? _d : (_e = speakerNode.children) === null || _e === void 0 ? void 0 : _e[0];
                const roleValue = editorSpeaker !== null
                    ? { label: editorSpeaker.role, isDefault: true }
                    : defaultChild != null
                        ? { label: defaultChild.title, isDefault: defaultChild.isDefault }
                        : null;
                setPinnedNodes([speakerNode]);
                const localization = dbSpeaker === null || dbSpeaker === void 0 ? void 0 : dbSpeaker.localizations[langCode];
                const speakerIdActive = isSpeakerIdEnabled
                    ? ((_f = localization === null || localization === void 0 ? void 0 : localization.speakerIdActive) !== null && _f !== void 0 ? _f : false)
                    : false;
                form.setFieldsValue(Object.assign({ speaker: {
                        speaker: speakerNode,
                        roleIndex: 'none',
                        firstName: speakerNode.firstName,
                        lastName: speakerNode.lastName,
                    }, role: roleValue, availability: speakerNode.savedInDb === true ? 'all' : 'current', speakerIdActive, imageBase64: speakerNode.imageBase64 }, (dbSpeaker
                    ? {
                        nameVariant: {
                            label: getFullName(editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.firstName, editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.lastName),
                            values: {
                                firstName: editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.firstName,
                                lastName: editorSpeaker === null || editorSpeaker === void 0 ? void 0 : editorSpeaker.lastName,
                            },
                            isDefault: speakerNode.isDefault,
                        },
                    }
                    : {})));
            }
        }
    }, []);
    useEffect(() => {
        if (editedId !== undefined) {
            handleEditIdChange(editedId, editedFirstName, editedLastName);
        }
    }, [editedId, editedFirstName, editedLastName]);
    const handleSubmit = async (formValues) => {
        var _a, _b, _c;
        setSubmitState('loading');
        const speakerNode = formValues.speaker.speaker;
        const updatedSpeaker = await editSpeaker(session.connection, speakerNode, formValues.role, formValues.availability, speakerData, langCode, nameVariantEntries, formValues.nameVariant, formValues.imageBase64, formValues.speakerIdActive);
        if (updatedSpeaker != null) {
            // Update all occurrences of the currently edited speaker in the document and in the catalog
            const oldSpeaker = (_a = getEditorSpeaker === null || getEditorSpeaker === void 0 ? void 0 : getEditorSpeaker(speakerNode.id)) !== null && _a !== void 0 ? _a : undefined;
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(updatedSpeaker, oldSpeaker, ReplaceMode.everywhere);
            // Replace the current occurrence of the selected speaker with the edited speaker
            if (((_b = replacedSpeaker === null || replacedSpeaker === void 0 ? void 0 : replacedSpeaker.dbSpeaker) === null || _b === void 0 ? void 0 : _b.neutralData.uniqueId) !== updatedSpeaker.dbid
                || ((_c = replacedSpeaker.editorSpeaker) === null || _c === void 0 ? void 0 : _c.id) !== updatedSpeaker.id) {
                onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(updatedSpeaker, undefined, ReplaceMode.current);
            }
            setSubmitState('idle');
            onClose();
        }
    };
    const handleAddRoleModalClose = () => {
        setTabChangeData === null || setTabChangeData === void 0 ? void 0 : setTabChangeData((prev) => (prev !== undefined ? Object.assign(Object.assign({}, prev), { openAddRoleModal: undefined }) : prev));
    };
    return (_jsxs(SpeakerForm, { form: form, onSubmit: handleSubmit, submitOnEnter: true, children: [_jsx(Form.Item, { name: "speaker", rules: [{ required: true, message: txt('speakerRequired') }], children: _jsx(SpeakerTreeSelect, { allowButtonActions: false, pinnedSpeakers: isReplacedMode ? pinnedNodes : [], onAvatarChange: (base64) => form.setFieldValue('imageBase64', base64) }) }), _jsx(SpeakerDetails, { disabled: !isCurrent, autofocusField: isCurrent ? 'firstName' : undefined, firstNamePath: ['speaker', 'speaker', 'firstName'], lastNamePath: ['speaker', 'speaker', 'lastName'] }), _jsx(NameVariantWithRole, { dbSpeaker: speakerData, init: initNameVariantEntries, role: role, nameVariant: nameVariant, autoFocus: isCurrent, isNameVariantRequired: isReplacedMode, openAddRoleModal: openAddRoleModal, documentRole: documentRole, onAddRoleModalClose: handleAddRoleModalClose }), _jsx(AvailabilitySettings, { disabled: isReplacedMode, hidden: { availability: mode !== 'select' } }), isCaptionMode && defaultSpeakerColor !== undefined && (_jsx(ColorPicker, { defaultSpeakerColor: defaultSpeakerColor }))] }));
};
export default EditTab;
